import { useState } from 'react';
import { ApiError, joinWaitlist } from '../../lib/api.js';
import { color, font, radius, shadow } from '../../theme/tokens.js';

/** The warm off-white everything sits in on the brand green. */
const CREAM = '#F7F4ED';

/**
 * The landing page's one interaction: a phone number and a button.
 *
 * A native `<form>` rather than a button with an onClick, so that pressing
 * Enter submits and mobile keyboards show a "Go" key — on a page whose whole
 * purpose is a single field, losing the Enter key costs real signups.
 *
 * Formatting happens as you type, but only for what looks like a US number.
 * Anything else is passed through untouched: reformatting a number the parser
 * doesn't understand makes it look broken while you type it, and the server
 * accepts an international `+` number anyway.
 *
 * Two palettes. `light` is the form on a pale surface; `onBrand` is the same
 * form standing on the brand green, where the input becomes a pane of the
 * background rather than a white box punched into it, and the button inverts to
 * cream so it is still the loudest thing on screen.
 *
 * @param {object} props
 * @param {string} [props.source]  Which surface this instance sits on.
 * @param {'light'|'onBrand'} [props.tone]
 */
export function WaitlistForm({ source = 'web-landing', tone = 'light' }) {
  const skin = SKINS[tone];
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const submitting = status === 'submitting';
  const ghost = ghostFor(phone);

  async function submit(event) {
    event.preventDefault();
    if (submitting) return;

    setStatus('submitting');
    setError(null);

    try {
      setResult(await joinWaitlist(phone, source));
      setStatus('joined');
    } catch (caught) {
      // A 503 means the database isn't wired up, which is our problem and not
      // something to phrase as though they typed it wrong.
      setError(
        caught instanceof ApiError && caught.status !== 500
          ? caught.message
          : 'Something went wrong on our end. Try again in a moment.'
      );
      setStatus('idle');
    }
  }

  if (status === 'joined') {
    return (
      <div style={{ ...styles.done, ...skin.done }} className="fade-in" role="status">
        <div style={{ ...styles.check, ...skin.check }} aria-hidden="true">
          ✓
        </div>
        <p style={{ ...styles.doneTitle, color: skin.doneTitleColor }}>
          {result?.alreadyJoined ? 'You’re already on the list' : 'You’re on the list'}
        </p>
        <p style={{ ...styles.doneBody, color: skin.bodyColor }}>
          {result?.position ? `Number ${result.position.toLocaleString()}. ` : ''}
          We’ll text you the moment Crave opens in your city.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} style={styles.form} noValidate>
      <div style={styles.row}>
        <label htmlFor="waitlist-phone" style={styles.srOnly}>
          Phone number
        </label>
        <div
          style={{
            ...styles.field,
            background: skin.fieldBackground,
            borderColor: error ? skin.errorBorder : skin.inputBorder,
          }}
        >
          {/* The unfilled half of the template, sitting behind a transparent
              input so a number appears to drop into a shape that was already
              waiting for it. Hidden from assistive tech and click-through: this
              is scenery, and the real control is on top of it. */}
          {ghost !== null && (
            <div aria-hidden="true" style={styles.ghost}>
              <span style={{ visibility: 'hidden' }}>{phone}</span>
              <span style={{ color: skin.ghostColor }}>{ghost}</span>
            </div>
          )}

          <input
            id="waitlist-phone"
            className={skin.inputClass}
            // `tel` rather than `text`: it opens the number pad on a phone, which
            // is the only keyboard this field ever wants.
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            // Only once the template has stepped aside for an international
            // number — otherwise the two would print on top of each other.
            placeholder={ghost === null ? '+44 20 7946 0958' : undefined}
            value={phone}
            onChange={(event) => setPhone(formatAsTyped(event.target.value))}
            disabled={submitting}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={error ? 'waitlist-error' : undefined}
            style={{ ...styles.input, ...skin.input }}
            onFocus={(event) => {
              const box = event.target.parentElement;
              box.style.borderColor = skin.focusBorder;
              box.style.boxShadow = `0 0 0 3px ${skin.focusRing}`;
            }}
            onBlur={(event) => {
              const box = event.target.parentElement;
              box.style.borderColor = error ? skin.errorBorder : skin.inputBorder;
              box.style.boxShadow = 'none';
            }}
          />
        </div>

        <button type="submit" disabled={submitting} style={{ ...styles.submit, ...skin.submit }}>
          {submitting && <span className="spinner" style={skin.spinner} />}
          {submitting ? 'Joining' : 'Join the waitlist'}
        </button>
      </div>

      {error ? (
        <p id="waitlist-error" role="alert" style={{ ...styles.error, color: skin.errorText }}>
          {error}
        </p>
      ) : (
        <p style={{ ...styles.hint, color: skin.hintColor }}>
          We text once, when Crave opens near you. Nowhere else.
        </p>
      )}
    </form>
  );
}

/**
 * The shape a US number is poured into. `#` is a digit, the rest is punctuation.
 *
 * Space-separated rather than the usual `(###) ###-####`: the empty slots are
 * drawn as dashes, so a hyphen between the last two groups disappeared into
 * them and an untouched field read as eight dashes in a row instead of as the
 * shape of a phone number.
 */
const TEMPLATE = '(###) ### ####';

/**
 * Figure dash (U+2012) — a dash defined to be exactly the width of a digit,
 * which is the whole reason it exists. So the empty slots read as the shape of
 * a phone number and the template does not jitter sideways as real numbers
 * land in them.
 *
 * A figure *space* (U+2007) is the same width and was the first thing tried
 * here — but it is a space, so the field showed `(614) 555-` trailing off into
 * nothing instead of showing what it was still waiting for.
 *
 * Written as an escape rather than as the character: at a glance it is an
 * ordinary hyphen, and quietly becoming one would break the alignment it is
 * here to provide.
 */
const DIGIT_SLOT = '‒';

/**
 * Formats a US number into {@link TEMPLATE} while it is being typed.
 *
 * Anything starting with `+` is left alone, so an international number can be
 * entered without the field fighting back — the server accepts those, and a
 * template cut for ten digits would mangle them.
 *
 * @param {string} value
 */
function formatAsTyped(value) {
  if (value.trim().startsWith('+')) return value.slice(0, 20);
  return fill(digitsOf(value)).filled;
}

/** @param {string} value */
function digitsOf(value) {
  return value.replace(/\D/g, '').slice(0, 10);
}

/**
 * Splits the template at the point the typed digits run out.
 *
 * `filled` is what the input holds and what is read in full colour; `rest` is
 * the remainder, drawn behind in a dimmer ink so the field shows the shape it
 * is waiting for instead of an example number that has to be read and then
 * discarded.
 *
 * The punctuation is why this cannot be two slices of one formatted string: the
 * `)` after the area code belongs to `filled` once three digits exist and to
 * `rest` before that.
 *
 * @param {string} digits  Digits only, at most ten.
 * @returns {{filled: string, rest: string}}
 */
function fill(digits) {
  let filled = '';
  let rest = '';
  // Punctuation seen since the last digit was placed. It cannot be committed
  // until the next digit arrives to earn it: written straight to `filled`, the
  // opening bracket appears in an empty field, and — since `filled` is what the
  // input holds — that bracket then has to be backspaced past to clear it.
  let pending = '';
  let used = 0;
  let ranOut = false;

  for (const slot of TEMPLATE) {
    if (slot !== '#') {
      if (ranOut) rest += slot;
      else pending += slot;
      continue;
    }

    if (used < digits.length) {
      filled += pending + digits[used++];
      pending = '';
    } else {
      ranOut = true;
      rest += pending + DIGIT_SLOT;
      pending = '';
    }
  }

  return { filled, rest: rest + pending };
}

/**
 * The template remainder to draw behind the field, or null when there is none
 * to draw — an international number has no fixed shape to promise.
 *
 * @param {string} value  Whatever the input currently holds.
 */
function ghostFor(value) {
  if (value.trim().startsWith('+')) return null;
  return fill(digitsOf(value)).rest;
}

/**
 * The two palettes.
 *
 * `onBrand` is not `light` with the colours swapped: on the brand green the
 * input reads better as a translucent pane of the background than as a white
 * box cut into it, and the button has to invert to cream to stay the loudest
 * thing on a field that is already saturated. The error red also lightens —
 * `color.danger` on green is close to a vibrating pair, and unreadable at 12px.
 */
const SKINS = {
  light: {
    inputClass: undefined,
    input: { color: color.ink },
    fieldBackground: color.surface,
    ghostColor: color.inkFaint,
    inputBorder: color.borderStrong,
    focusBorder: color.primary,
    focusRing: color.primarySoft,
    errorBorder: color.danger,
    errorText: color.danger,
    hintColor: color.inkMuted,
    submit: { background: color.primary, color: color.inkInverse, boxShadow: shadow.brand },
    spinner: undefined,
    done: { background: color.primarySoft, border: `1.5px solid ${color.primaryBorder}` },
    check: { background: color.primary, color: color.inkInverse },
    doneTitleColor: color.primaryDark,
    bodyColor: color.inkMuted,
  },

  onBrand: {
    inputClass: 'field-on-brand',
    input: { color: CREAM },
    fieldBackground: 'rgba(255,255,255,0.14)',
    ghostColor: 'rgba(247,244,237,0.44)',
    inputBorder: 'rgba(247,244,237,0.46)',
    focusBorder: CREAM,
    focusRing: 'rgba(247,244,237,0.22)',
    errorBorder: '#FFB4A6',
    errorText: '#FFC9BE',
    hintColor: 'rgba(247,244,237,0.78)',
    submit: { background: CREAM, color: color.primaryDark },
    // The default spinner is white on a coloured button; here the button is
    // cream, so it has to darken or it vanishes mid-submit.
    spinner: { borderColor: 'rgba(47,69,20,0.25)', borderTopColor: color.primaryDark },
    done: { background: 'rgba(255,255,255,0.12)', border: '1.5px solid rgba(247,244,237,0.30)' },
    check: { background: CREAM, color: color.primaryDark },
    doneTitleColor: CREAM,
    bodyColor: 'rgba(247,244,237,0.82)',
  },
};

const styles = {
  form: { width: '100%' },

  // Stacks on a phone and sits side by side from the first breakpoint where
  // both halves still have room to be tapped comfortably.
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
  },

  /**
   * The border and background moved out of the input and onto this wrapper, so
   * the ghost template can sit inside the same box rather than on top of it.
   * Everything that positions a glyph — padding, size, tracking, digit width —
   * is repeated identically on `input` and `ghost` below, and the two will
   * visibly disagree if they ever drift apart.
   */
  field: {
    position: 'relative',
    flex: '1 1 220px',
    display: 'flex',
    // The ghost is `white-space: pre` so its slots cannot wrap mid-template,
    // which means on a narrow phone it runs past the end of the field. Without
    // this it pushed the whole page sideways.
    overflow: 'hidden',
    // 48px tall: above the 44px touch-target floor, and matches the button so
    // the pair reads as one control when they sit on the same line.
    minHeight: 48,
    borderRadius: radius.lg,
    borderWidth: 1.5,
    borderStyle: 'solid',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },

  ghost: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    padding: '13px 16px',
    fontSize: 16,
    letterSpacing: '0.4px',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'pre',
    pointerEvents: 'none',
  },

  input: {
    flex: 1,
    minWidth: 0,
    padding: '13px 16px',
    border: 'none',
    borderRadius: radius.lg,
    background: 'transparent',
    fontSize: 16, // Anything smaller makes iOS Safari zoom the page on focus.
    letterSpacing: '0.4px',
    fontVariantNumeric: 'tabular-nums',
    outline: 'none',
  },

  submit: {
    flex: '1 1 auto',
    minHeight: 48,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    padding: '13px 24px',
    borderRadius: radius.lg,
    border: 'none',
    fontSize: 15,
    fontWeight: font.weight.bold,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },

  // `inkMuted`, not `inkFaint`: this now sits on a glass panel rather than on
  // solid canvas, and the faint grey came out around 2.2:1 against it —
  // under the 4.5:1 that body text this size needs.
  hint: { margin: '12px 2px 0', fontSize: 12.5, lineHeight: 1.5, textAlign: 'center' },
  error: {
    margin: '12px 2px 0',
    fontSize: 12.5,
    lineHeight: 1.5,
    fontWeight: font.weight.semibold,
    textAlign: 'center',
  },

  done: { padding: '26px 24px', borderRadius: radius.xl, textAlign: 'center' },
  check: {
    width: 38,
    height: 38,
    margin: '0 auto 12px',
    borderRadius: radius.pill,
    fontSize: 19,
    fontWeight: font.weight.bold,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  doneTitle: {
    margin: '0 0 6px',
    fontSize: 17,
    fontWeight: font.weight.heavy,
    letterSpacing: '-0.3px',
  },
  doneBody: { margin: 0, fontSize: 13.5, lineHeight: 1.6 },

  // Visually hidden but still read aloud — the placeholder is not a label.
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
};
